import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import useWorkspace from "../workspaces/useWorkspace";
import { useState } from "react";
import { AppConfirm } from "../../../utils/PopupUtil";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { deletePageInPages } from "../../../utils/PageUtils";
import { newToastEvent } from "../../../utils/ToastUtil";
import PageAPI from "../../api/PageAPI";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { useAtomValue } from "jotai";

const useDeletePage = () => {
  const { workspace } = useWorkspace();
  const workspaceID = useAtomValue(workspaceIdAtom);
  const [deletingID, setDeletingID] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantID, pageID: urlPageID } = useParams();

  const deletePage = useMutation({
    mutationFn: ({
      pageID,
      onSuccess,
      isApp,
    }: {
      pageID: string;
      onSuccess?: () => void;
      isApp?: boolean;
    }) => {
      return AppConfirm({
        title: "Confirm Page Deletion",
        html: "Are you sure you want to delete this Page? <br/> All content inside will be lost.",
      })
        .then((result) => {
          if (!result.isConfirmed) return;

          setDeletingID(pageID);

          return PageAPI.deletePage(
            workspace.id || workspaceID,
            pageID,
            isApp
          ).then((data) => {
            deletePageInPages(queryClient, pageID, workspace.id);
            queryClient.invalidateQueries({
              queryKey: ["workspaces", tenantID],
            });
            queryClient.invalidateQueries({
              queryKey: ["workspace", workspace.id],
            });

            newToastEvent("success", "Successfully removed your page.");

            //Redirect if user is currently on the page deleted.
            if (urlPageID === pageID) {
              navigate(
                `/clouds/${tenantID}/workspaces/${workspace.id}/home/pages`
              );
            }

            callFunctionIfExists(onSuccess);
          });
        })
        .catch((e) => {
          console.error(e);
          setDeletingID(null);
        });
    },

    onError: (e) => {
      console.error(e);
    },
    onSettled: () => {
      setDeletingID(null);
    },
  });

  return {
    deletePage: deletePage.mutate,
    pageDeleting: deletePage.isPending,
    deletePageID: deletingID,
  };
};

export default useDeletePage;
